// Three-way merge for PDF annotations (highlights, pen strokes, shapes), shared by the web app and
// the phone app (StudyBoard-Android/utils/annotationMerge.ts is a copy — keep them identical).
//
// Annotations are stored per page as a list of strokes, in one document
// users/{uid}/notebooks/{nb}/extra/pdfAnnotations = { data: { [page]: Stroke[] } }.
// Strokes have no ids, so a stroke is identified by its content. Each device remembers the
// version it last saw from the server ("base"); its changes are what it added and removed since
// then. Saving applies only those changes on top of the server's current version, so one device
// never deletes or overwrites strokes another device added.

export type Annotations = Record<string, any[]>;

const stable = (v: any): string => {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  return '{' + Object.keys(v).filter(k => v[k] !== undefined).sort().map(k => JSON.stringify(k) + ':' + stable(v[k])).join(',') + '}';
};
export const strokeSig = (s: any) => stable(s);

const pages = (...maps: (Annotations | undefined)[]) =>
  Array.from(new Set(maps.flatMap(m => Object.keys(m ?? {}))));

/** Result = theirs, minus what we removed since base, plus what we added since base. */
export function mergeAnnotations(base: Annotations | undefined, ours: Annotations | undefined, theirs: Annotations | undefined): Annotations {
  const out: Annotations = {};
  for (const p of pages(base, ours, theirs)) {
    const b = new Set((base?.[p] ?? []).map(strokeSig));
    const o = new Set((ours?.[p] ?? []).map(strokeSig));
    const removed = new Set([...b].filter(s => !o.has(s)));
    const kept = (theirs?.[p] ?? []).filter(s => !removed.has(strokeSig(s)));
    const have = new Set(kept.map(strokeSig));
    const added = (ours?.[p] ?? []).filter(s => { const k = strokeSig(s); return !b.has(k) && !have.has(k) && (have.add(k), true); });
    const list = [...kept, ...added];
    if (list.length) out[p] = list;
  }
  return out;
}

export function sameAnnotations(a: Annotations | undefined, b: Annotations | undefined): boolean {
  for (const p of pages(a, b)) {
    const x = (a?.[p] ?? []).map(strokeSig), y = (b?.[p] ?? []).map(strokeSig);
    if (x.length !== y.length || x.some((s, i) => s !== y[i])) return false;
  }
  return true;
}
